/**
 * form.ts — the form builder and the two form accessors (P1 US-112, FR-19).
 *
 * Ported from oracle lines 823–867: `champHTML`, `construire`, `lire`, `ecrire`.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THE COERCION IN `lire` IS PART OF THE CONTRACT, NOT AN ACCIDENT
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * `el.type === 'number' ? parseFloat(el.value) : el.value` (oracle line 859) has two consequences
 * that look like bugs and are neither:
 *
 *  1. **An empty numeric field yields `NaN`, not `0` and not `undefined`.** The request schema
 *     rejects `NaN` for every field where it matters, deliberately — `UNITS.md` G7 measured what a
 *     `NaN` does to the untouched engine, and the answer is that a check silently *disappears*
 *     from the result while `ok` stays `true`. Coercing empties to `0` here would hide that behind
 *     a plausible-looking number instead of surfacing it as a validation error.
 *  2. **A `<select>` always yields a string, including `classeService`, which is conceptually the
 *     number 2.** The contract's `ClasseServiceSchema` accepts both forms precisely because this
 *     is what the tool has always sent. Do not "fix" it here: the saved-file format contains the
 *     string, so a UI that started sending a number would write files the current tool cannot read.
 */

import { $$, must } from './dom';
import { FICHES, type Champ, type FicheModule } from './fiches';

/** One field's markup. Oracle lines 823–840, structure for structure. */
function champHTML(mod: FicheModule, c: Champ): string {
  const id = `${mod}_${c.id}`;
  let h = '';

  h += `<div class="row" data-f="${c.id}">`;
  h += `<label for="${id}">${c.l}${c.s !== undefined ? ` <em>${c.s}</em>` : ''}</label>`;

  if (c.t === 'sel') {
    h += `<select id="${id}" data-id="${c.id}">`;
    for (const o of c.o ?? []) {
      // `String(o) === String(c.d)` rather than `o === c.d`: `classeService` has numeric options
      // and its default arrives as a number, while option values are text. The oracle's own
      // comparison, kept for the same reason it was written that way.
      h += `<option${String(o) === String(c.d) ? ' selected' : ''}>${o}</option>`;
    }
    h += '</select>';
  } else if (c.t === 'text') {
    h += `<input type="text" id="${id}" data-id="${c.id}" value="${c.d}">`;
  } else {
    // The default branch. A field with no `t` is a number input, and `step` defaults to 1.
    h += `<input type="number" id="${id}" data-id="${c.id}" value="${c.d}" step="${c.step ?? 1}">`;
  }

  h += `<span class="u">${c.u ?? ''}</span>`;
  h += '</div>';

  if (c.h !== undefined) h += `<div class="hint">${c.h}</div>`;
  return h;
}

/**
 * Build a module's form and wire its change listeners. Oracle lines 842–854.
 *
 * `onChange` is a parameter rather than a direct call to a `calculer` in this module, and that is
 * the one structural change from the oracle. In the oracle the listener calls the calculation
 * synchronously on **every keystroke**, which was free in-page and is one HTTP request per
 * keystroke over a network. US-113 owns the debounce, the abort and the ordering; this file just
 * reports that something changed.
 */
export function construire(
  mod: FicheModule,
  onChange: (mod: FicheModule) => void,
): HTMLFormElement {
  const fiche = FICHES[mod];
  let h = '';

  for (const groupe of fiche.groupes) {
    h += `<fieldset><legend>${groupe.titre}</legend>`;
    for (const c of groupe.champs) h += champHTML(mod, c);
    h += '</fieldset>';
  }

  const form = must<HTMLFormElement>(`#form_${mod}`);
  form.innerHTML = h;

  for (const el of $$<HTMLInputElement | HTMLSelectElement>(
    `#form_${mod} input, #form_${mod} select`,
  )) {
    el.addEventListener('input', () => onChange(mod));
    el.addEventListener('change', () => onChange(mod));
  }

  return form;
}

/** A form's current values. Oracle lines 856–862. */
export function lire(mod: FicheModule): Record<string, string | number> {
  const values: Record<string, string | number> = {};

  for (const el of $$<HTMLInputElement | HTMLSelectElement>(`#form_${mod} [data-id]`)) {
    const key = el.dataset['id'];
    if (key === undefined) continue;
    // See the file header: `parseFloat` of an empty string is NaN, and that is load-bearing.
    values[key] = el.type === 'number' ? Number.parseFloat(el.value) : el.value;
  }

  return values;
}

/**
 * Write values back into a form. Oracle lines 863–867.
 *
 * Skips keys the object does not carry, so a saved file written by an older version — or a
 * candidate from the optimiser that only names a section — updates the fields it knows about and
 * leaves the rest alone.
 */
export function ecrire(mod: FicheModule, values: Record<string, unknown>): void {
  for (const el of $$<HTMLInputElement | HTMLSelectElement>(`#form_${mod} [data-id]`)) {
    const key = el.dataset['id'];
    if (key === undefined) continue;
    const value = values[key];
    if (value !== undefined) el.value = String(value);
  }
}
